/**
 * Adult ASD SSI Review — Diagnostic criteria review page.
 *
 * Stacked review layout (same column as Observation Review):
 *
 *   Summary table  — each symptom criterion (A1–B4) with Childhood and
 *                    Adulthood evidence pills, plus an optional domain-impact
 *                    table when Domains Impacted is enabled for the org.
 *
 *   Decision panels — Confidence, Referred For, Differentials, Formulation,
 *                     and Confidential Notes. All share REVIEW_COLUMN_CLASS
 *                     so the table and panels are the same width.
 *
 * Data source: ModularPatientDataContext assessmentStages.semiStructuredInterview
 * Writes: Review-specific fields (confidence, differentials, notes) are persisted
 * to assessmentStages/semiStructuredInterview.review via updateAssessmentStage.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef, useContext } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { SearchIcon, XIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { useVideoStream } from '../../../context/VideoStreamContext';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import AppointmentChecklistControls from '../../../components/autismModular/AppointmentChecklistControls';
import { DocumentReportIcon } from '@heroicons/react/outline';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import ConfidenceLevelSelector, { TERNARY_OPTIONS } from '../../../components/autismReview/ConfidenceLevelSelector';
import ConditionSelectionPanelTOC from '../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import NotInReportNotesPanel from '../../../components/autismReview/NotInReportNotesPanel';
import AutismReferredForPanel from '../../../components/autismReview/AutismReferredForPanel';
import { ASD_CONDITION_LABEL, getAutismDifferentialConditions } from '../../../constants/autismConditions';
import { normaliseNotesValue } from '../../../utils/autismReviewData';
import SSI_CRITERIA, { FUNCTIONAL_IMPAIRMENT_DOMAINS, hasAsdSsiCriteriaInput } from './config/adultAsdSSIConfig';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { getAutismHistorySectionTitles } from '../../../utils/autismDocumentActions';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstCompletedFields } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { AUTISM_EVIDENCE_PILL_COLOURS } from '../../../constants/autismEvidenceColors';
import { renderNoteWithQuotes } from '../../../utils/highlightQuotes';

/**
 * Normalises notes into a flat array of `{ id, text }` objects, filtering out
 * empty entries. Handles both the StructuredNotesEditor array format and
 * legacy HTML/plain-text strings (which become a single-element array).
 */
function normaliseNotesArray(notesValue) {
  if (!notesValue) return [];
  if (Array.isArray(notesValue)) {
    return notesValue.filter(n => n && String(n.text || '').trim());
  }
  const trimmed = String(notesValue).trim();
  if (!trimmed) return [];
  return [{ id: 'legacy', text: trimmed }];
}

/**
 * Evidence pill colours — Clear = red, No evidence = green (shared Autism scale).
 */
const EVIDENCE_PILL = AUTISM_EVIDENCE_PILL_COLOURS;

function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">&mdash;</span>;
  const style = EVIDENCE_PILL[value] || { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block w-[7.5rem] text-center px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

/**
 * Derives whether Criterion A is met:
 * All three of A1, A2, A3 must have "Clear evidence" in BOTH life stages.
 */
function prefillCriterionA(allData) {
  const aIds = ['A1', 'A2', 'A3'];
  const allDecided = aIds.every(id => {
    const d = allData[id];
    return d?.evidence_childhood && d?.evidence_adulthood;
  });
  if (!allDecided) return null;

  return aIds.every(id => {
    const d = allData[id];
    return d.evidence_childhood === 'Clear evidence' && d.evidence_adulthood === 'Clear evidence';
  });
}

/**
 * Derives whether Criterion B is met:
 * At least 2 of B1-B4 must have "Clear evidence" in BOTH life stages.
 */
function prefillCriterionB(allData) {
  const bIds = ['B1', 'B2', 'B3', 'B4'];
  const allDecided = bIds.every(id => {
    const d = allData[id];
    return d?.evidence_childhood && d?.evidence_adulthood;
  });
  if (!allDecided) return null;

  const metCount = bIds.filter(id => {
    const d = allData[id];
    return d.evidence_childhood === 'Clear evidence' && d.evidence_adulthood === 'Clear evidence';
  }).length;

  return metCount >= 2;
}

/**
 * Shared column for the stacked Review page: DSM summary table, optional
 * domain-impact table, and the decision panels below. Previously the table
 * stretched full-width when domains were hidden while the panels stayed at
 * half-width, so they did not line up. `max-w-5xl` matches Observation Review.
 */
const REVIEW_COLUMN_CLASS = 'w-full max-w-5xl';

export default function AdultAsdSSIReview() {
  const { patientId } = useParams();
  const location = useLocation();
  const navigate = usePreviewAwareNavigate();
  const videoStream = useVideoStream();

  const {
    header,
    assessmentStages,
    updateAssessmentStage,
    flushStageWrites,
    reportContent,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const permissionModel = header || {};
  const canEdit = useCanEditStage(permissionModel, 'SSI', { previewMode: isPreviewMode });
  const { claims } = useAuth();
  const wf = useWorkflowConfig(header, claims);
  const showDomains = wf?.isSSIDomainsImpactedEnabled !== false;

  useEffect(() => {
    videoStream?.setVideoEnabled(true);
    return () => videoStream?.setVideoEnabled(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ssiStageData = assessmentStages?.semiStructuredInterview;

  // Criteria data from Firestore — the source of truth for symptom evidence
  const allData = useMemo(() => {
    return ssiStageData?.data?.criteria || {};
  }, [ssiStageData]);

  const criterionAPrefill = useMemo(() => prefillCriterionA(allData), [allData]);
  const criterionBPrefill = useMemo(() => prefillCriterionB(allData), [allData]);

  // Review-specific data (confidence, differentials, notes) from Firestore
  const savedReview = useMemo(() => {
    return ssiStageData?.review || {};
  }, [ssiStageData]);

  /**
   * Initialise decisions — merge prefill values for A and B into saved
   * decisions. The prefill only applies when the saved review doesn't
   * already have an explicit value for that criterion (undefined or absent).
   */
  const [decisions, setDecisions] = useState(() => {
    const saved = savedReview?.decisions || {};
    return {
      A: saved.A !== undefined ? saved.A : criterionAPrefill,
      B: saved.B !== undefined ? saved.B : criterionBPrefill,
      C: saved.C !== undefined ? saved.C : null,
      D: saved.D !== undefined ? saved.D : null,
      E: saved.E !== undefined ? saved.E : null,
    };
  });

  // eslint-disable-next-line no-unused-vars
  const handleDecision = (criterion, value) => {
    setDecisions(prev => ({
      ...prev,
      [criterion]: prev[criterion] === value ? null : value,
    }));
  };

  const [confidence, setConfidence] = useState(() => savedReview?.confidence ?? null);
  const [confidenceTernary, setConfidenceTernary] = useState(() => savedReview?.confidenceTernary ?? null);
  // eslint-disable-next-line no-unused-vars
  const [confidenceStyle, _setConfidenceStyle] = useState(() => savedReview?.confidenceStyle ?? 'ternary');

  // Confidential Notes (amber, never used in the report).
  const [formulationNotes, setFormulationNotes] = useState(
    () => normaliseNotesValue(savedReview?.formulationNotes),
  );
  const handleNotesChange = useCallback((val) => setFormulationNotes(val), []);

  // Emerging Formulation (amber, never used in the report) — new section.
  const [emergingFormulation, setEmergingFormulation] = useState(
    () => normaliseNotesValue(savedReview?.emergingFormulation),
  );

  // Emerging differentials — a "select many" picker over the categorised ICD-11
  // list (ASD excluded; it is the condition referred for). This mirrors the ADHD
  // SSI Review page (ConditionSelectionPanelTOC) so the whole platform records
  // differentials the same way. Clinicians capture their per-condition reasoning
  // in the Emerging Formulation box below rather than against each condition.
  const ICD_DIFFERENTIAL_CONDITIONS = useMemo(() => getAutismDifferentialConditions(), []);

  // ASD is defensively filtered from any legacy saved data so it can never appear
  // in both the referred-for panel and the differentials list.
  const [formulationConditions, setFormulationConditions] = useState(
    () => (savedReview?.formulationConditions || []).filter(c => c !== ASD_CONDITION_LABEL),
  );
  const [differentialsConfirmed, setDifferentialsConfirmed] = useState(
    () => savedReview?.differentialsConfirmed ?? false,
  );

  // Toggle a differential on/off in the multi-select picker.
  const handleToggleCondition = useCallback((condition) => {
    setFormulationConditions(prev =>
      prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]);
  }, []);

  // Confirm/Change-choice workflow collapses the picker into a read-only summary.
  const handleConfirmDifferentials = useCallback(() => setDifferentialsConfirmed(true), []);
  const handleChangeDifferentials = useCallback(() => setDifferentialsConfirmed(false), []);

  // Persist review state to Firestore via the centralised write API.
  // Uses a ref to track whether initial hydration is complete so the first
  // render doesn't trigger a spurious write back to Firestore. This is the
  // SINGLE writer for review state — individual handlers only touch local state.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) { hydrated.current = true; return; }
    if (!canEdit) return;
    updateAssessmentStage('semiStructuredInterview', {
      'review.decisions': decisions,
      'review.confidence': confidence,
      'review.confidenceTernary': confidenceTernary,
      'review.confidenceStyle': confidenceStyle,
      'review.formulationNotes': formulationNotes,
      'review.emergingFormulation': emergingFormulation,
      'review.formulationConditions': formulationConditions,
      'review.differentialsConfirmed': differentialsConfirmed,
    });
  }, [canEdit, updateAssessmentStage, decisions, confidence, confidenceTernary, confidenceStyle, formulationNotes, emergingFormulation, formulationConditions, differentialsConfirmed]);

  // ---- Summary data for the left table ----
  const symptomRows = useMemo(() =>
    SSI_CRITERIA.filter(c => c.available).map(c => {
      const d = allData[c.id] || {};
      return {
        id: c.id,
        shortTitle: c.shortTitle,
        fullTitle: c.fullTitle,
        childhoodEvidence: d.evidence_childhood || null,
        adulthoodEvidence: d.evidence_adulthood || null,
        domainsChildhood: d.domains_childhood || [],
        domainsAdulthood: d.domains_adulthood || [],
        notesChildhood: normaliseNotesArray(d.notes_childhood),
        notesAdulthood: normaliseNotesArray(d.notes_adulthood),
        askedQuestions: d.asked_questions || [],
      };
    }),
  [allData]);

  /** Last available criterion — used by the footer Back button to return from Review. */
  const lastCriterion = useMemo(() => {
    const available = SSI_CRITERIA.filter(c => c.available !== false);
    return available[available.length - 1] || null;
  }, []);

  /**
   * Domain impact summary — for each FI domain, derive the highest evidence
   * level across all symptoms for each life stage.
   */
  const EVIDENCE_PRIORITY = ['Clear evidence', 'Partial evidence', 'No evidence', 'Inconclusive'];

  const domainRows = useMemo(() => {
    if (!showDomains) return [];
    return FUNCTIONAL_IMPAIRMENT_DOMAINS.map(domain => {
      const childhoodSymptoms = {};
      const adulthoodSymptoms = {};

      EVIDENCE_PRIORITY.forEach(lvl => { childhoodSymptoms[lvl] = []; adulthoodSymptoms[lvl] = []; });

      symptomRows.forEach(row => {
        if (row.domainsChildhood?.includes(domain.id) && row.childhoodEvidence) {
          childhoodSymptoms[row.childhoodEvidence]?.push(`${row.id}. ${row.shortTitle}`);
        }
        if (row.domainsAdulthood?.includes(domain.id) && row.adulthoodEvidence) {
          adulthoodSymptoms[row.adulthoodEvidence]?.push(`${row.id}. ${row.shortTitle}`);
        }
      });

      const deriveLevel = (buckets) => {
        for (const lvl of EVIDENCE_PRIORITY) {
          if (buckets[lvl].length > 0) return lvl;
        }
        return null;
      };

      return {
        domain,
        childhoodLevel: deriveLevel(childhoodSymptoms),
        adulthoodLevel: deriveLevel(adulthoodSymptoms),
        childhoodSymptoms,
        adulthoodSymptoms,
      };
    });
  },
    [showDomains, symptomRows], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    ['Diagnostic Developmental History', `/patient/${patientId}/adult-autism/history`],
    ['Semi-Structured Interview', `/patient/${patientId}/proforma/adult-asd/semi-structured-interview`],
    ['Review'],
  ];

  const [detailPopup, setDetailPopup] = useState(null);

  // ---- Completion flow state ----
  // Controls whether the confirmation dialog (recapping evidence before generation) is visible
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Controls whether the progress dialog (showing real-time section generation status) is visible
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Sidebar navigation — clicking a criterion navigates back to the SSI
   * page with that criterion active. Flush pending Review writes first
   * so the SSI page sees the latest data.
   */
  const handleSidebarNavigate = useCallback(async (itemId) => {
    if (itemId === 'REVIEW') return;
    await flushStageWrites('semiStructuredInterview');
    navigate(
      `/patient/${patientId}/proforma/adult-asd/semi-structured-interview`,
      { state: { activeItemId: itemId } },
    );
  }, [navigate, patientId, flushStageWrites]);

  /** Navigate back to History, flushing any pending Review writes first. */
  const handleBackToHistory = useCallback(async () => {
    await flushStageWrites('semiStructuredInterview');
    navigate(`/patient/${patientId}/adult-autism/history`);
  }, [flushStageWrites, navigate, patientId]);

  // ---- Completion flow handlers ----

  // Confidence level is mandatory — the Finish button is disabled until the
  // clinician records a confidence rating (either ternary or percentage).
  const isConfidenceRecorded = confidenceStyle === 'percentage'
    ? confidence !== null
    : !!confidenceTernary;

  /**
   * Opens the completion dialog. Before showing the dialog, flushes any
   * pending review field writes to Firestore so the dialog's evidence
   * summary reflects the latest saved state.
   */
  const handleComplete = useCallback(async () => {
    if (!canEdit || !isConfidenceRecorded) return;
    await flushStageWrites('semiStructuredInterview');
    setIsDialogOpen(true);
  }, [canEdit, isConfidenceRecorded, flushStageWrites]);

  /**
   * Handles the "Generate Report" click from inside the completion dialog.
   *
   * This is the main completion action for the SSI stage. It performs
   * several things in sequence:
   *
   * 1. Marks the SSI assessment stage as completed (status, progress, audit fields)
   * 2. Triggers bulk catch-up generation: for any SSI or History report sections that
   *    haven't been generated yet, fires off a Cloud Function call to draft the narrative.
   *    This ensures all relevant report sections are queued even if the clinician skipped
   *    individual section-level generation during the interview.
   * 3. Shows the progress dialog so the clinician can watch sections being generated.
   *
   * Note: we deliberately do NOT write the `${prefix}ReportGenerated` audit flags
   * to the reportContent docs from the client. In the modular Autism data model the
   * Documents Dashboard derives "ready-for-review" from the presence of generated
   * `outputs` (written server-side by the Cloud Function), so a client flag write is
   * both redundant and fragile (reportContent docs are clinician/CF-write-only and may
   * not exist yet, which previously surfaced a spurious "changes not saved" toast).
   */
  const handleFinishSSI = useCallback(async () => {
    setIsDialogOpen(false);

    // Mark both SSI and History stages as completed — they form a single
    // "Diagnostic Developmental History" appointment, so finishing SSI
    // completes the entire appointment. Without the History completion
    // marker the Patient Overview progress bar stays at 0% because the
    // isStarted guard gates on inProgress|completed status.
    //
    // `lastCompletedAt` is refreshed on every completion; `firstCompletedAt`
    // (plus By/ByName) is write-once — buildFirstCompletedFields returns {}
    // when the stage already carries it, so a re-completion never moves the
    // original date. This is the same helper and the same semantics the ADHD
    // proformas use; the only difference is where the stage lives (a modular
    // assessmentStages/{stage} doc rather than dataModel.N_stage). It is also
    // what the Patient Overview card uses as the "estimated" appointment date.
    // Each stage is checked against its OWN doc: a patient assessed before
    // this field existed has lastCompletedAt but no firstCompletedAt, so the
    // first re-completion under this code stamps it (harmless and expected).
    const ssiFirstCompleted = buildFirstCompletedFields(assessmentStages?.semiStructuredInterview);
    const historyFirstCompleted = buildFirstCompletedFields(assessmentStages?.history);
    const completionMeta = {
      status: 'completed',
      progress: 1,
      lastCompletedAt: serverTimestamp(),
      lastCompletedBy: auth.currentUser?.uid,
      lastCompletedByName: auth.currentUser?.displayName,
    };
    updateAssessmentStage('semiStructuredInterview', { ...completionMeta, ...ssiFirstCompleted });
    updateAssessmentStage('history', { ...completionMeta, ...historyFirstCompleted });

    // Inferred appointment-date audit (fire-and-forget; writes to the
    // appointmentDateHistory subcollection only, never to appointmentDates).
    // History and SSI are one appointment on the overview, so they share the
    // single `ddi` module key and we record ONE entry for the pair. It fires
    // if EITHER stage was completed for the first time: both are stamped
    // together above, so on a genuine first completion both are new, and on a
    // re-completion both already carry firstCompletedAt and nothing fires.
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ddi',
      didWriteFirstCompleted:
        Object.keys(ssiFirstCompleted).length > 0 || Object.keys(historyFirstCompleted).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    // Bulk catch-up generation: check which sections have already been
    // generated (tracked in stageSectionsGenerated arrays) and trigger
    // generation for any that are missing. This handles the case where
    // the clinician moved through the interview without generating
    // individual sections along the way.
    const ssiReport = reportContent?.semiStructuredInterview || {};
    const historyReport = reportContent?.history || {};
    const ssiGenerated = ssiReport.stageSectionsGenerated || [];
    const historyGenerated = historyReport.stageSectionsGenerated || [];

    // Flush all pending writes (SSI completion + History completion + any
    // buffered data) so the Cloud Function sees the latest data.
    const dataReady = flushStageWrites();

    // SSI narrative is a single "Criteria" report section (per-DSM-criterion
    // narrative). Functional Impairment has been folded into the criteria pages,
    // so there is no separate FI report section to generate.
    // Guard: only fire when the clinician has recorded evidence, notes, or
    // domains on at least one criterion. If they used "Proceed Anyway" with
    // no evidence, data.criteria is empty and extract_criteria_inputs would
    // return {}, causing main.py to abort with a 500. Using the Firestore
    // snapshot is correct here because flushStageWrites was called before
    // navigating to this page, so the snapshot is current.
    const ssiCriteria = assessmentStages?.semiStructuredInterview?.data?.criteria;
    if (!ssiGenerated.includes('Criteria') && hasAsdSsiCriteriaInput(ssiCriteria)) {
      TriggerGenerateDocumentSection(
        patientId, 'Autism Semi-Structured Interview', 'Criteria', [], dataReady,
      );
    }

    // History catch-up: trigger generation for any History sections that
    // haven't been generated yet. Derived from the patient's seeded history
    // data so the list self-adjusts when sections are removed by the form
    // profile (e.g. Forensic History is absent for Owl Centre Autism patients).
    const historySections = getAutismHistorySectionTitles(
      assessmentStages, wf.isCIRiskAssessmentEnabled,
    );
    for (const sectionTitle of historySections) {
      if (!historyGenerated.includes(sectionTitle)) {
        TriggerGenerateDocumentSection(
          patientId, 'Autism History', sectionTitle, [], dataReady,
        );
      }
    }

    setIsGenerating(true);
  }, [
    patientId,
    updateAssessmentStage,
    reportContent,
    flushStageWrites,
    assessmentStages,
    wf.isCIRiskAssessmentEnabled,
    wf.isAppointmentDatesEnabled,
  ]);

  /** Dismiss the progress dialog without navigating (e.g. if generation is cancelled). */
  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  /** Navigate to the Documents page after all report sections have been generated. */
  const handleAfterGeneration = useCallback(() => {
    navigate(`/patient/${patientId}/adult-autism/documents`);
  }, [navigate, patientId]);

  const showChecklist = wf.isAppointmentChecklistEnabled;

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0">
        <Breadcrumb
          tupleList={breadcrumbTuples}
          allowWrap={showChecklist}
          rightSlot={showChecklist
            ? <AppointmentChecklistControls appointmentType="developmentalHistory" />
            : undefined
          }
        />
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left sidebar — shared ModularSideNavbar (compact). Review is the
            active extra row here; clicking a criterion returns to the SSI page
            with that criterion active. Module exit is in the fixed footer. */}
        <ModularSideNavbar
          mode="compact"
          activeId="REVIEW"
          onSelect={handleSidebarNavigate}
          items={SSI_CRITERIA.map((c) => {
            const d = allData?.[c.id];
            const complete = !!(d?.evidence_childhood && d?.evidence_adulthood);
            const started = !!(d?.evidence_childhood || d?.evidence_adulthood);
            return {
              id: c.id,
              badge: c.id,
              label: c.shortTitle,
              fullTitle: c.fullTitle,
              title: c.shortTitle,
              status: complete ? 'completed' : started ? 'started' : 'default',
              isUnlocked: c.available !== false,
            };
          })}
          extraRows={[
            {
              id: 'REVIEW',
              label: 'Review',
              badge: <DocumentReportIcon className="w-3.5 h-3.5" />,
              active: true,
              onClick: () => handleSidebarNavigate('REVIEW'),
            },
          ]}
        />

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto p-6 text-left">

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Review of Semi-Structured Interview
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Summary of evidence across Childhood and Adulthood presentation by DSM criteria.
          </p>
        </div>

        <div className="flex flex-col items-center gap-6">

          {/* ======== Summary table ======== */}
          <div className={REVIEW_COLUMN_CLASS}>
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full text-left">
              <colgroup>
                <col style={{ width: '40%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '30%' }} />
              </colgroup>
              <thead>
                <tr className="bg-primary text-white">
                  <th className="p-3 text-sm font-bold">DSM Criteria</th>
                  <th className="p-3 text-sm font-bold text-center">Childhood</th>
                  <th className="p-3 text-sm font-bold text-center">Adulthood</th>
                </tr>
              </thead>
              <tbody>
                {symptomRows.map((row, i) => {
                  const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                  const renderStageCell = (evidence, notes, stageLabel) => {
                    const hasDetail = notes.length > 0 || row.askedQuestions.length > 0;
                    return (
                      <td className="p-3 align-top text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <EvidencePill value={evidence} />
                          {hasDetail && (
                            <button
                              onClick={() => setDetailPopup({
                                type: 'symptom',
                                symptomId: row.id,
                                shortTitle: row.shortTitle,
                                stage: stageLabel,
                                evidence,
                                askedQuestions: row.askedQuestions,
                                notes,
                              })}
                              className="text-gray-400 hover:text-primary transition-colors duration-150"
                              title="View details"
                            >
                              <SearchIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  };

                  return (
                    <tr key={row.id} className={`${bgClass} border-b border-gray-200`}>
                      <td className="p-3 align-top">
                        <p className="text-sm text-gray-900 font-bold leading-snug">
                          {row.id}. {row.shortTitle}
                        </p>
                      </td>
                      {renderStageCell(row.childhoodEvidence, row.notesChildhood, 'Childhood')}
                      {renderStageCell(row.adulthoodEvidence, row.notesAdulthood, 'Adulthood')}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* ======== Domain impact table ======== */}
          {showDomains && <div className={REVIEW_COLUMN_CLASS}>
            <div className="rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full text-left">
                <colgroup>
                  <col style={{ width: '40%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-3 text-sm font-bold">Domain</th>
                    <th className="p-3 text-sm font-bold text-center">Childhood</th>
                    <th className="p-3 text-sm font-bold text-center">Adulthood</th>
                  </tr>
                </thead>
                <tbody>
                  {domainRows.map((row, i) => {
                    const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                    const renderDomainStageCell = (level, symptomBuckets, stageLabel) => {
                      const hasAny = EVIDENCE_PRIORITY.some(lvl => symptomBuckets[lvl]?.length > 0);
                      return (
                        <td className="p-3 align-top text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <EvidencePill value={level} />
                            {hasAny && (
                              <button
                                onClick={() => setDetailPopup({
                                  type: 'domain',
                                  domainTitle: row.domain.title,
                                  stage: stageLabel,
                                  symptomBuckets,
                                })}
                                className="text-gray-400 hover:text-primary transition-colors duration-150"
                                title="View breakdown"
                              >
                                <SearchIcon className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    };

                    return (
                      <tr key={row.domain.id} className={`${bgClass} border-b border-gray-200 last:border-b-0`}>
                        <td className="p-3 align-top">
                          <p className="text-sm text-gray-900 font-bold leading-snug">
                            {row.domain.title}
                          </p>
                        </td>
                        {renderDomainStageCell(row.childhoodLevel, row.childhoodSymptoms, 'Childhood')}
                        {renderDomainStageCell(row.adulthoodLevel, row.adulthoodSymptoms, 'Adulthood')}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>}

          {/* ======== Confidence Level ======== */}
          <ConfidenceLevelSelector
            className={REVIEW_COLUMN_CLASS}
            confidenceStyle={confidenceStyle}
            confidence={confidence}
            confidenceTernary={confidenceTernary}
            onConfidenceChange={setConfidence}
            onConfidenceTernaryChange={setConfidenceTernary}
            canEdit={canEdit}
            description="Based on the Semi-Structured Interview, how likely is it in your clinical opinion that this person is autistic?"
          />

          {/* ======== Condition(s) Referred For ======== */}
          {/* Read-only panel — for an Autism assessment the referred condition is
              always ASD, so it is shown as a fixed label (mirrors the ADHD review,
              which shows the referred ADHD condition the same way). Uses the shared
              compact Autism panel so its fonts match the rest of the review page. */}
          <AutismReferredForPanel className={REVIEW_COLUMN_CLASS} />

          {/* ======== Emerging Differentials ======== */}
          {/* "Select many" picker over the categorised ICD-11 list (ASD excluded).
              Autism variant: compact fonts (parity with the other review panels),
              a substring search box, and a running list of selected conditions.
              Per-condition reasoning is recorded in the Emerging Formulation box
              below, not against each row. */}
          {/* TOC skips per-differential decisions in DD so differentials
              are informational only → amber + "Not included" footer. If an
              org enables DD differential decisions the panel reverts to the
              default gray chrome, matching the DD Review page logic
              (skipAutismDifferentialsDecisions). */}
          <div className={REVIEW_COLUMN_CLASS}>
            <ConditionSelectionPanelTOC
              title="Emerging Differentials"
              variant={wf.skipAutismDifferentialsDecisions ? 'non-report' : 'default'}
              density="compact"
              searchable
              showSelectedBullets
              subtitle="Conditions you're actively considering as alternative explanations, co-occurring conditions, or to rule in/out at Diagnostic Decision. Capture your reasoning in the Emerging Formulation below."
              selectedOptions={formulationConditions}
              isMultiSelect
              isConfirmed={differentialsConfirmed}
              onSelect={handleToggleCondition}
              onConfirm={handleConfirmDifferentials}
              onChangeChoice={handleChangeDifferentials}
              conditions={ICD_DIFFERENTIAL_CONDITIONS}
              allowEmptyConfirm
              canEdit={canEdit}
            />
          </div>

          {/* ======== Emerging Formulation (not in final report) ======== */}
          {/* Language mirrors the ADHD SSI Review (3_SSIReview.js); the Autism
              difference is the structured-notes box instead of a free-text area. */}
          <NotInReportNotesPanel
            className={REVIEW_COLUMN_CLASS}
            title="Emerging Formulation"
            description="Your working hypothesis about what's going on for this person and why. Capture the presenting picture, contributing factors, functional impact and strengths. Finalised at Diagnostic Decision."
            notes={emergingFormulation}
            onNotesChange={setEmergingFormulation}
            canEdit={canEdit}
            domainId="emergingFormulation"
            placeholder="Type a note and press Enter…"
          />

          {/* ======== Confidential Notes (not in final report) ======== */}
          <NotInReportNotesPanel
            className={REVIEW_COLUMN_CLASS}
            title="Confidential Notes"
            description="Confidential or sensitive observations for review at Diagnostic Decision."
            notes={formulationNotes}
            onNotesChange={handleNotesChange}
            canEdit={canEdit}
            domainId="confidentialNotes"
            placeholder="Type a note and press Enter…"
          />

        </div>
        </div>

        {/* Fixed footer — back to last criterion within SSI; History as phase
            skip link; Finish as the terminal forward CTA. */}
        <ModularStepFooter
          showBack={!!lastCriterion}
          backLabel={lastCriterion?.id}
          onBack={lastCriterion ? () => handleSidebarNavigate(lastCriterion.id) : undefined}
          phaseBackLink={{ label: 'Back to History', onClick: handleBackToHistory }}
          showNext={false}
          rightActions={canEdit ? (
            <button
              type="button"
              onClick={handleComplete}
              disabled={!isConfidenceRecorded}
              className={`flex items-center py-2 px-5 rounded-md shadow-sm whitespace-nowrap transition-all duration-200 ${
                isConfidenceRecorded
                  ? 'text-white bg-primary hover:bg-primary/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <span className="font-semibold text-sm">Finish Semi-Structured Interview</span>
              <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />
            </button>
          ) : undefined}
        />
        </div>
      </div>

      {/* ---- Detail popup overlay ---- */}
      {detailPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDetailPopup(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setDetailPopup(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            {detailPopup.type === 'symptom' && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-0.5">
                  {detailPopup.symptomId}. {detailPopup.shortTitle}
                </h2>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-500">{detailPopup.stage}</span>
                  <EvidencePill value={detailPopup.evidence} />
                </div>

                {detailPopup.notes && detailPopup.notes.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Notes</h3>
                    <ul className="space-y-1.5">
                      {detailPopup.notes.map((note) => (
                        <li key={note.id} className="flex items-start gap-2">
                          <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                          <span className="text-sm text-gray-700 leading-relaxed">{renderNoteWithQuotes(note.text)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {detailPopup.askedQuestions.length > 0 && (
                  <div>
                    <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">Questions Marked as Asked</h3>
                    <div className="flex flex-col gap-1.5">
                      {detailPopup.askedQuestions.map((q, idx) => (
                        <div
                          key={idx}
                          className="px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {detailPopup.type === 'domain' && (
              <>
                <h2 className="text-base font-bold text-gray-900 mb-0.5">
                  {detailPopup.domainTitle}
                </h2>
                <p className="text-xs text-gray-500 mb-4">{detailPopup.stage} — Evidence breakdown by DSM criteria</p>

                {EVIDENCE_PRIORITY.map(level => {
                  const symptoms = detailPopup.symptomBuckets[level];
                  if (!symptoms || symptoms.length === 0) return null;
                  const pill = EVIDENCE_PILL[level];
                  return (
                    <div key={level} className="mb-3 last:mb-0">
                      <div className="mb-1">
                        <span className={`inline-block px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${pill?.bg || 'bg-gray-100'} ${pill?.text || 'text-gray-600'}`}>
                          {level}
                        </span>
                      </div>
                      <ul className="space-y-0.5 pl-1">
                        {symptoms.map(s => (
                          <li key={s} className="flex items-start gap-2">
                            <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                            <span className="text-sm text-gray-700 leading-snug">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Completion dialog: recaps evidence decisions and differentials ---- */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Semi-Structured Interview Complete"
        description={<>Review the evidence summary before generating the report.</>}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleFinishSSI} />
          </>
        }
      >
        <SummarySection title="Criteria Evidence:">
          {/* Column headers match the on-page summary table: Childhood first,
              then Adulthood. The empty w-8 spacer lines up with the criterion
              IDs so the labels sit above the pills. */}
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 mb-1">
            <span className="w-8" aria-hidden="true" />
            <span className="w-[7.5rem] text-center">Childhood</span>
            <span className="w-[7.5rem] text-center">Adulthood</span>
          </div>
          {symptomRows.map(row => (
            <div key={row.id} className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-gray-800 w-8">{row.id}</span>
              <EvidencePill value={row.childhoodEvidence} />
              <EvidencePill value={row.adulthoodEvidence} />
            </div>
          ))}
        </SummarySection>

        <SummarySection title="Condition referred for:">
          <p className="text-sm text-gray-700">{ASD_CONDITION_LABEL}</p>
        </SummarySection>

        <SummarySection title="Confidence level:">
          <p className="text-sm text-gray-700">
            {confidenceStyle === 'percentage'
              ? (confidence !== null ? `${confidence}%` : <span className="italic text-gray-400">Not recorded</span>)
              : (confidenceTernary
                ? TERNARY_OPTIONS.find(o => o.value === confidenceTernary)?.label || confidenceTernary
                : <span className="italic text-gray-400">Not recorded</span>)}
          </p>
        </SummarySection>

        <SummarySection title="Emerging differentials under consideration:">
          {formulationConditions.length > 0 ? (
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
              {formulationConditions.map(c => <li key={c}>{c}</li>)}
            </ul>
          ) : (
            <p className="text-sm italic text-gray-400">No conditions selected</p>
          )}
        </SummarySection>

        {/* Emerging formulation recap — handles both structured-notes arrays
            (normaliseNotesValue output) and legacy plain strings. Always
            rendered; shows "Not provided" when empty so the clinician sees
            a reminder that nothing was recorded. */}
        <SummarySection title="Emerging formulation:">
          {(() => {
            const MAX_ITEMS = 3;
            const items = Array.isArray(emergingFormulation)
              ? emergingFormulation.map(n => n?.text || '').filter(Boolean)
              : (typeof emergingFormulation === 'string' && emergingFormulation.trim() ? [emergingFormulation] : []);
            const visible = items.slice(0, MAX_ITEMS);
            const remaining = items.length - MAX_ITEMS;
            if (items.length === 0) {
              return <p className="text-sm italic text-gray-400">Not provided</p>;
            }
            return (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                  {visible.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                {remaining > 0 && (
                  <p className="text-xs text-gray-400 mt-1 pl-5">+ {remaining} more</p>
                )}
              </div>
            );
          })()}
        </SummarySection>

        {/* Confidential notes recap — gray in the popup (the amber
            report-exclusion treatment belongs on the page panels, not in
            the ephemeral confirmation summary). Same bullet-point
            rendering as formulation above. */}
        <SummarySection title="Confidential notes:">
          {(() => {
            const MAX_ITEMS = 3;
            const items = Array.isArray(formulationNotes)
              ? formulationNotes.map(n => n?.text || '').filter(Boolean)
              : (typeof formulationNotes === 'string' && formulationNotes.trim() ? [formulationNotes] : []);
            const visible = items.slice(0, MAX_ITEMS);
            const remaining = items.length - MAX_ITEMS;
            if (items.length === 0) {
              return <p className="text-sm italic text-gray-400">Not provided</p>;
            }
            return (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-0.5">
                  {visible.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                {remaining > 0 && (
                  <p className="text-xs text-gray-400 mt-1 pl-5">+ {remaining} more</p>
                )}
              </div>
            );
          })()}
        </SummarySection>

        <DialogMessage>
          Generating the report will create AI-drafted narrative paragraphs for
          each criterion. You will review and approve each paragraph before it is
          included in the final report.
        </DialogMessage>
      </ModuleCompletionDialog>

      {/* ---- Progress dialog: shows real-time section generation progress ---- */}
      <GenerateReportProgressDialog
        reportStage="Semi-Structured Interview"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
        completionButtonLabel="Go to Documents"
      />

      <style>{`
        @keyframes fadeScaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideOut {
          from { opacity: 1; transform: translateY(0); max-height: 200px; }
          to   { opacity: 0; transform: translateY(-6px); max-height: 0; }
        }
      `}</style>
      </div>
    </Layout>
  );
}
